/**
 * P135/P801 onboarding tutorial: contextual tip cards for the first session of
 * each start mode. Approved design (mock 2026-10-23, user-approved 12:01):
 * parchment card above the tab bar, NEXT / DISMISS TIP / SKIP ALL, dismissal
 * and skip persisted per device (localStorage), replay from Settings.
 *
 * Every tip references a real, verified control (Herd select-and-move,
 * press-hold Feed, Park enclosure.build, Office staff deploy / licence /
 * insurance, Map parcel.buy) and composes numbers from the live sim/catalog
 * at render time - nothing hardcodes costs or capacities.
 */
import { useSyncExternalStore } from 'preact/compat';
import type { UiState } from './bridge';
import type { TabId } from './store';
import { FENCE_PRICE_PER_M_CENTS } from '../sim/systems/landtools';

export interface TutorialSegment { text: string; bold?: boolean }

export interface TutorialTip {
  id: string;
  /** tab whose control carries out the step; highlighted while the tip shows */
  tab: TabId;
  body: (sim: UiState) => TutorialSegment[];
  /** gate: the step only makes sense once this holds (sequence pauses until it does) */
  when: (sim: UiState) => boolean;
  /** the step is complete in the sim itself */
  done: (sim: UiState) => boolean;
}

export interface TutorialPersist {
  /** DISMISS TIP - never returns unless replayed from Settings */
  dismissed: string[];
  /** NEXT - move on; the tip still auto-completes if the sim reaches its goal */
  advanced: string[];
  skippedAll: boolean;
  finished: boolean;
}

export const TUTORIAL_STORAGE_KEY = 'ea.tutorial.v1';

export const FRESH_TUTORIAL: TutorialPersist = { dismissed: [], advanced: [], skippedAll: false, finished: false };

const cents = (n: number) => `$${Math.round(n / 100).toLocaleString('en-US')}`;

/** build cost, same arithmetic as ParkScreen costOf (base + fence at grade rate + water).
 *  4A: fence is grade-priced, so the tip quotes the BASIC-grade total as the
 *  honest entry price ("from $X with basic fencing") - the player's grade pick
 *  sets the actual price; nothing hardcodes a fixed cost. */
const buildCost = (sim: UiState, type: string, fencePerimeterM: number, waterPoints: number, grade = 1) => {
  const t = sim.enclosureTypes[type];
  return t.base_cost_cents + (FENCE_PRICE_PER_M_CENTS[grade] ?? 0) * fencePerimeterM + t.water_point_cost_cents * waterPoints;
};

/** head capacity of an enclosure, same arithmetic as the build rows (density x area) */
const capacityOf = (sim: UiState, enclosureId: string) => {
  const enc = sim.enclosures.find((e) => e.id === enclosureId);
  const t = enc ? sim.enclosureTypes[enc.type] : undefined;
  return enc && t ? Math.floor(enc.areaHa * t.density_per_ha) : 0;
};

const housed = (sim: UiState) => sim.enclosures.some((e) => e.animalCount > 0);
const keeperDeployed = (sim: UiState) => sim.staff.some((s) => s.assignedTo !== null);

export const TUTORIAL_SCRIPTS: Record<string, TutorialTip[]> = {
  standard: [
    {
      id: 'std-house', tab: 'herd',
      body: (sim) => {
        const pen = sim.enclosures.at(0);
        const cap = pen ? capacityOf(sim, pen.id) : 0;
        return [
          { text: `Your herd is at the gate - ${String(sim.animals.length)} animals, and your holding pen has room for ${String(cap)}. ` },
          { text: 'Move a pair in from the Herd tab.', bold: true },
        ];
      },
      when: (sim) => sim.animals.some((a) => a.enclosure === null),
      done: (sim) => sim.enclosures.some((e) => e.animalCount >= 2) || sim.animals.every((a) => a.enclosure !== null),
    },
    {
      id: 'std-feed', tab: 'herd',
      body: () => [
        { text: 'Hungry animals lose condition. Open an animal and ' },
        { text: 'press and hold Feed', bold: true },
        { text: ' - one hold feeds the whole pen.' },
      ],
      when: housed,
      done: () => false,
    },
    {
      id: 'std-pasture', tab: 'park',
      body: (sim) => [
        { text: 'Room to grow. A 2 ha pasture with fence and a water point costs from ' },
        { text: `${cents(buildCost(sim, 'pasture', 400, 1))} with basic fencing`, bold: true },
        { text: ' - you pick the fence grade, and heavier grades cost more per metre.' },
        { text: ' - build it from the Park tab.' },
      ],
      when: () => true,
      done: (sim) => sim.enclosures.length > 1,
    },
    {
      id: 'std-keeper', tab: 'office',
      body: () => [
        { text: 'Your keeper is waiting for work. ' },
        { text: 'Deploy them to a pen from the Office tab.', bold: true },
      ],
      when: (sim) => sim.staff.length > 0,
      done: keeperDeployed,
    },
    {
      id: 'std-loan', tab: 'office',
      body: (sim) => [
        { text: `You started with a ${cents(sim.loan?.principal ?? 0)} loan at ${String(sim.loan?.aprPercent ?? 0)}%. ` },
        { text: 'Pay it down from the Office tab', bold: true },
        { text: ' when cash allows.' },
      ],
      when: (sim) => sim.loan !== null,
      done: (sim) => sim.loan === null,
    },
  ],
  easy: [
    {
      id: 'easy-feed', tab: 'herd',
      body: () => [
        { text: 'Your herd is settled in. Hungry animals lose condition - open an animal and ' },
        { text: 'press and hold Feed', bold: true },
        { text: ' - one hold feeds the whole pen.' },
      ],
      when: (sim) => sim.animals.length > 0,
      done: () => false,
    },
    {
      id: 'easy-keeper', tab: 'office',
      body: () => [
        { text: 'Your keeper is waiting for work. ' },
        { text: 'Deploy them to a pen from the Office tab.', bold: true },
      ],
      when: (sim) => sim.staff.length > 0,
      done: keeperDeployed,
    },
    {
      id: 'easy-map', tab: 'map',
      body: () => [
        { text: 'Your reserve at a glance: parcels, prices and land for sale live on the ' },
        { text: 'Map tab.', bold: true },
      ],
      when: () => true,
      done: () => false,
    },
  ],
  hard: [
    {
      id: 'hard-licence', tab: 'office',
      body: () => [
        { text: 'Hard mode starts with no keeping licence or insurance. ' },
        { text: 'Buy both from the Office tab first.', bold: true },
      ],
      when: (sim) => !sim.licence || !sim.insurance,
      done: (sim) => sim.licence && sim.insurance,
    },
    {
      id: 'hard-land', tab: 'map',
      body: (sim) => {
        const forSale = sim.parcels.filter((p) => !p.owned).sort((a, b) => a.price - b.price);
        const cheapest = forSale.at(0);
        return [
          { text: 'Buy your first parcel from the Map tab. ' },
          cheapest
            ? { text: `The cheapest land on sale is ${cheapest.landClass.replace(/_/g, ' ')} - ${String(cheapest.hectares)} ha for ${cents(cheapest.price)}.`, bold: true }
            : { text: 'Land for sale is listed there.', bold: true },
        ];
      },
      when: (sim) => sim.licence,
      done: (sim) => sim.parcels.some((p) => p.owned),
    },
    {
      id: 'hard-pen', tab: 'park',
      body: (sim) => [
        { text: 'Now build a pen on your land from the Park tab - a holding pen costs from ' },
        { text: `${cents(buildCost(sim, 'holding_pen', 120, 1))} with basic fencing`, bold: true },
        { text: '.' },
      ],
      when: (sim) => sim.parcels.some((p) => p.owned),
      done: (sim) => sim.enclosures.length > 0,
    },
    {
      id: 'hard-house', tab: 'herd',
      body: (sim) => {
        const pen = sim.enclosures.at(0);
        const cap = pen ? capacityOf(sim, pen.id) : 0;
        return [
          { text: `Your herd is still at the gate. Your pen has room for ${String(cap)} - ` },
          { text: 'move animals in from the Herd tab.', bold: true },
        ];
      },
      when: (sim) => sim.enclosures.length > 0 && sim.animals.some((a) => a.enclosure === null),
      done: (sim) => sim.animals.every((a) => a.enclosure !== null) || sim.enclosures.some((e) => e.animalCount >= 1),
    },
  ],
};

export function loadTutorial(read: (key: string) => string | null): TutorialPersist {
  try {
    const raw = read(TUTORIAL_STORAGE_KEY);
    if (!raw) return { ...FRESH_TUTORIAL };
    const p = JSON.parse(raw) as Partial<TutorialPersist>;
    return {
      dismissed: Array.isArray(p.dismissed) ? p.dismissed.filter((x): x is string => typeof x === 'string') : [],
      advanced: Array.isArray(p.advanced) ? p.advanced.filter((x): x is string => typeof x === 'string') : [],
      skippedAll: p.skippedAll === true,
      finished: p.finished === true,
    };
  } catch {
    return { ...FRESH_TUTORIAL };
  }
}

export function saveTutorial(write: (key: string, value: string) => void, state: TutorialPersist): void {
  write(TUTORIAL_STORAGE_KEY, JSON.stringify(state));
}

const resolved = (tip: TutorialTip, state: TutorialPersist, sim: UiState) =>
  state.dismissed.includes(tip.id) || state.advanced.includes(tip.id) || tip.done(sim);

export function isComplete(script: TutorialTip[], state: TutorialPersist, sim: UiState): boolean {
  return script.every((tip) => resolved(tip, state, sim));
}

/** When the sequence waits on a closed gate, the last advanced-but-unfinished step the player was
 *  pointed at, so the card can say what is actually pending instead of vanishing. Null when nothing
 *  actionable is owed (then the card stays hidden rather than inventing an instruction). */
export function pendingTip(script: TutorialTip[], state: TutorialPersist, sim: UiState): TutorialTip | null {
  if (state.skippedAll || state.finished) return null;
  const waiting = script.find((tip) => !resolved(tip, state, sim));
  if (waiting === undefined || waiting.when(sim)) return null;
  for (let i = script.indexOf(waiting) - 1; i >= 0; i--) {
    const tip = script[i];
    if (state.advanced.includes(tip.id) && !state.dismissed.includes(tip.id) && !tip.done(sim)) return tip;
  }
  return null;
}

/** first unresolved tip whose gate is open; null while the sequence waits on the player */
export function activeTip(script: TutorialTip[], state: TutorialPersist, sim: UiState): { tip: TutorialTip; index: number; total: number } | null {
  if (state.skippedAll || state.finished) return null;
  for (let i = 0; i < script.length; i++) {
    const tip = script[i];
    if (resolved(tip, state, sim)) continue;
    if (!tip.when(sim)) return null;
    return { tip, index: i + 1, total: script.length };
  }
  return null;
}

export function nextTip(state: TutorialPersist, tipId: string): TutorialPersist {
  return { ...state, advanced: [...new Set([...state.advanced, tipId])] };
}

export function dismissTip(state: TutorialPersist, tipId: string): TutorialPersist {
  return { ...state, dismissed: [...new Set([...state.dismissed, tipId])] };
}

export function skipAllTips(state: TutorialPersist): TutorialPersist {
  return { ...state, skippedAll: true };
}

/** Settings > Replay tutorial: wipe the record so the sequence runs again */
export function replayTutorial(): TutorialPersist {
  return { ...FRESH_TUTORIAL };
}

/* -------- device-persisted reactive store (browser only) -------- */

type Listener = () => void;

function createTutorialStore() {
  const listeners = new Set<Listener>();
  let state = loadTutorial((k) => globalThis.localStorage.getItem(k));
  const persist = (next: TutorialPersist) => {
    state = next;
    try { saveTutorial((k, v) => { globalThis.localStorage.setItem(k, v); }, state); } catch { /* private mode: session-only */ }
    listeners.forEach((fn) => { fn(); });
  };
  return {
    getState: () => state,
    subscribe: (fn: Listener) => { listeners.add(fn); return () => { listeners.delete(fn); }; },
    next: (tipId: string) => { persist(nextTip(state, tipId)); },
    dismiss: (tipId: string) => { persist(dismissTip(state, tipId)); },
    skipAll: () => { persist(skipAllTips(state)); },
    replay: () => { persist(replayTutorial()); },
    markFinished: () => { persist({ ...state, finished: true }); },
  };
}

export const tutorial = createTutorialStore();

export function useTutorialState(): TutorialPersist {
  return useSyncExternalStore(tutorial.subscribe, tutorial.getState);
}

/** the tab the active tip points at (for the TabBar highlight), or null */
export function activeTutorialTab(script: TutorialTip[], state: TutorialPersist, sim: UiState | null): TabId | null {
  if (!sim) return null;
  return activeTip(script, state, sim)?.tip.tab ?? null;
}

export function scriptForMode(mode: string | null): TutorialTip[] {
  return TUTORIAL_SCRIPTS[mode ?? ''] ?? TUTORIAL_SCRIPTS.standard;
}
